import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, x12Date, fmtNum, ts } from "@/lib/api";
import { DeleteOrderDialog, canDelete } from "@/components/DeleteOrderDialog";
import { PageHeader, Panel, Th, Td, Empty, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { EdiImportDialog } from "@/components/EdiImportDialog";

const STATUSES = {
  customer: ["", "received", "released", "shipped", "short_shipped", "sap_only", "cancelled", "archived"],
  replenishment: ["", "received", "in_transit", "delivered", "discrepancy", "cancelled"],
};

export default function Orders({ type = "customer" }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [imp, setImp] = useState(false);
  const [del, setDel] = useState(null);
  const arch = status === "archived";
  const load = () => (arch ? api.get("/orders/archive", { params: { order_type: type } }) : api.get("/orders", { params: { order_type: type, status: status || undefined } })).then((r) => setRows(r.data));
  const restore = async (o) => {
    try {
      await api.post(`/orders/archive/${o.id}/restore`);
      toast.success(`PO ${o.po_number} restored`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const purge = async (o) => {
    if (!window.confirm(`Permanently delete PO ${o.po_number}? It cannot be restored, and any SAP IDoc for it that has not been sent is removed. The PO number can then be used again.`)) return;
    try {
      await api.delete(`/orders/archive/${o.id}`);
      toast.success(`PO ${o.po_number} permanently deleted`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    load();
  }, [type, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCust = type === "customer";
  return (
    <div>
      <PageHeader
        title={isCust ? "Customer orders" : "Replenishment orders"}
        subtitle={isCust ? "Orders fulfilled from Group O xDock stock — 940 Warehouse Shipping Order out, 945 Warehouse Shipping Advice back." : "Group O 850 Purchase Orders to restock xDock sites — ship from EP, 861 Receiving Advice back."}
      >
        <Button onClick={() => setImp(true)} className={btn.primary} data-testid="orders-import-850-btn">
          <Upload className="mr-1.5 h-4 w-4" /> Import 850 Purchase Order
        </Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="orders-status-filter">
        {STATUSES[type].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            data-testid={`orders-filter-${s || "all"}`}
            className={`rounded-sm border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
              status === s ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-800 text-slate-400 hover:text-white"
            }`}
          >
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
      </div>
      <Panel>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="orders-table">
              <thead className="border-b border-slate-800">
                <tr><Th>PO</Th><Th>Depositor #</Th><Th>Ship to</Th><Th>Site</Th><Th>Lines</Th><Th>Units</Th><Th>PO date</Th><Th>Required</Th><Th>Created</Th><Th>Status</Th>{arch && <Th>Deleted</Th>}{isCust && <Th></Th>}</tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((o) => (
                  <tr key={o.id} onClick={() => !arch && nav(`/orders/${o.id}`)} data-testid={`order-row-${o.po_number}`} className="cursor-pointer transition-colors hover:bg-slate-800/40">
                    <Td className="font-mono text-slate-100">{o.po_number}</Td>
                    <Td className="font-mono">{o.depositor_order_no}</Td>
                    <Td className="max-w-[220px] truncate">{o.parties?.ST?.name || "—"}</Td>
                    <Td className="font-mono">{o.site_code || <span className="text-amber-300">unset</span>}</Td>
                    <Td>{o.lines.length}</Td>
                    <Td>{fmtNum(o.lines.reduce((a, l) => a + l.qty, 0))}</Td>
                    <Td>{x12Date(o.po_date)}</Td>
                    <Td>{x12Date(o.delivery_date)}</Td>
                    <Td className="whitespace-nowrap text-xs" data-testid={`order-created-${o.po_number}`}>{o.created_at ? ts(o.created_at) : "—"}</Td>
                    <Td><StatusBadge status={o.status} /></Td>
                    {arch && <Td className="whitespace-nowrap text-xs">{ts(o.archived_at)} · {o.archived_by}</Td>}
                    {isCust && (
                      <Td onClick={(e) => e.stopPropagation()}>
                        {arch ? (
                          <span className="inline-flex gap-1">
                            <button onClick={() => restore(o)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-emerald-300 transition-colors hover:bg-emerald-500/10" data-testid={`order-restore-${o.po_number}`}><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                            <button onClick={() => purge(o)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-red-400 transition-colors hover:bg-red-500/10" data-testid={`order-purge-${o.po_number}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                          </span>
                        ) : canDelete(o) && (
                          <button onClick={() => setDel(o)} className="rounded-sm p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400" aria-label="Delete order" data-testid={`order-delete-${o.po_number}`}><Trash2 className="h-4 w-4" /></button>
                        )}
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty testId="orders-empty">{arch ? "No deleted orders in the archive." : "No orders yet."}</Empty>
        )}
      </Panel>
      <EdiImportDialog open={imp} onOpenChange={setImp} onDone={load} title="Import 850 Purchase Order" />
      <DeleteOrderDialog order={del} onOpenChange={(v) => !v && setDel(null)} onDone={() => { setDel(null); load(); }} />
    </div>
  );
}
